import { isAdminRole, REGIONAL_ROLES, summaryOf } from '@utils/stationScope';

const MODES = [
  { id: 'home',     title: 'Home station only', hint: 'Sees data for the home station and nothing else' },
  { id: 'regional', title: 'Regional',          hint: 'Sees several stations, for example the islands' },
  { id: 'all',      title: 'All stations',      hint: 'Head office: every airport and head office' },
];

const labelCls = 'text-2xs font-mono uppercase tracking-widest text-gl-muted';
const inputCls =
  'w-full px-3 py-2 rounded-lg text-sm text-gl-ghost placeholder-gl-muted bg-gl-surface border border-gl-border focus:outline-none focus-visible:ring-2 focus-visible:ring-gl-ghost';

/**
 * Station access section of the user drawer. `form` is { role, mode, home, scope };
 * `stations` is the registry from GET /stations.
 */
export function StationAccess({ form, setForm, stations }) {
  const adminRole = isAdminRole(form.role);
  const mode = adminRole ? 'all' : form.mode;
  const active = stations.filter((s) => s.is_active);
  const airports = active.filter((s) => s.kind === 'airport');
  const homeOptions = mode === 'all' ? active : airports;

  const setMode = (m) => setForm((f) => ({ ...f, mode: m }));
  const setHome = (code) => setForm((f) => ({
    ...f,
    home: code,
    scope: !code || f.scope.includes(code) ? f.scope : [...f.scope, code],
  }));
  const toggle = (code) => setForm((f) => ({
    ...f,
    scope: f.scope.includes(code) ? f.scope.filter((c) => c !== code) : [...f.scope, code],
  }));

  return (
    <fieldset className="space-y-3 border-t border-gl-border pt-4">
      <legend className={`${labelCls} pr-2`}>Station access</legend>

      <div>
        <label htmlFor="user-home-station" className={labelCls}>
          Home station{mode === 'all' ? ' (default view)' : ''}
        </label>
        <select
          id="user-home-station"
          className={inputCls}
          value={form.home}
          disabled={adminRole}
          onChange={(e) => setHome(e.target.value)}
        >
          <option value="">Choose…</option>
          {homeOptions.map((s) => <option key={s.code} value={s.code}>{s.code} · {s.name}</option>)}
        </select>
      </div>

      <div role="radiogroup" aria-label="Station scope" className="grid gap-2">
        {MODES.map((m) => (
          <label
            key={m.id}
            className={`relative block text-left rounded-xl px-3 py-2.5 transition-colors has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-gl-ghost ${adminRole ? 'opacity-45 cursor-not-allowed' : 'cursor-pointer'}`}
            style={mode === m.id
              ? { background: 'rgba(45,125,70,0.14)', border: '1px solid rgba(74,174,73,0.7)' }
              : { background: 'transparent', border: '1px solid #24405F' }}
          >
            <input
              type="radio"
              name="station-scope-mode"
              value={m.id}
              checked={mode === m.id}
              disabled={adminRole}
              onChange={() => setMode(m.id)}
              className="absolute inset-0 w-full h-full opacity-0 cursor-[inherit]"
            />
            <span className="block text-sm text-gl-ghost">{m.title}</span>
            <span className="block text-xs text-gl-muted">{m.hint}</span>
          </label>
        ))}
      </div>

      {mode === 'regional' && (
        <div>
          <p className={labelCls}>Stations covered</p>
          <div className="flex flex-wrap gap-2 mt-1">
            {airports.map((s) => {
              const isHome = form.home === s.code;
              const on = isHome || form.scope.includes(s.code);
              return (
                <button
                  type="button"
                  key={s.code}
                  aria-pressed={on}
                  disabled={isHome}
                  onClick={() => toggle(s.code)}
                  className="font-mono text-sm px-3 py-2 rounded-lg disabled:cursor-default"
                  style={on
                    ? { background: '#0b0b0b', border: '1px solid #F5C800', color: '#F5C800' }
                    : { background: '#13263A', border: '1px solid #24405F', color: '#F7F8FA' }}
                >
                  {s.code}
                </button>
              );
            })}
          </div>
          <p className="text-xs text-gl-muted mt-1">The home station is always included.</p>
        </div>
      )}

      {adminRole && <p className="text-xs text-gl-muted">Admins always see all stations. This cannot be narrowed.</p>}

      {REGIONAL_ROLES.includes(form.role) && mode === 'home' && (
        <p className="text-xs rounded-lg px-3 py-2" style={{ background: 'rgba(232,160,0,0.09)', border: '1px solid rgba(232,160,0,0.5)', color: '#F3C766' }}>
          This is usually a regional role. Choose Regional if this person covers more than one station.
        </p>
      )}

      <p data-testid="station-summary" className="text-sm rounded-xl px-3 py-2.5 bg-gl-surface border border-gl-border text-gl-ghost">
        This user will see: <span style={{ color: '#4AAE49' }}>{summaryOf({ ...form, mode })}</span>
      </p>

      <p className="text-xs text-gl-muted">
        Only admins can change station access. A change reaches platforms within 15 minutes; removing access signs the user out at once.
      </p>
    </fieldset>
  );
}
